import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';

/**
 * Route guard that validates the presence of required query parameters before allowing route activation.
 *
 * This guard checks if a specific query parameter (defined in route data) exists in the current route's query parameters.
 * If the parameter is not required (no `checkParam` in route data), the guard allows access.
 *
 * @example
 * ```typescript
 * // In your routing configuration:
 * {
 *   path: 'details',
 *   component: DetailsComponent,
 *   canActivate: [ParameterGuard],
 *   data: { checkParam: 'id' } // This will check for ?id=xxx in the URL
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Route with required parameter:
 * // URL: /details?id=123 -> ✅ Access allowed
 * // URL: /details -> ❌ Access denied
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class ParameterGuard {
  /**
   * Determines if a route can be activated based on the presence of required query parameters.
   *
   * This method is called by the Angular router before activating a route. It checks if the route
   * requires a specific query parameter (via `route.data.checkParam`) and validates its presence.
   *
   * @param route - The activated route snapshot containing route data and query parameters
   * @param state - The router state snapshot (not used in this guard)
   * @returns `true` if the route can be activated, `false` if access should be denied, or a `UrlTree` for redirection
   *
   * @example
   * ```typescript
   * // Route configuration:
   * {
   *   path: 'user',
   *   component: UserComponent,
   *   canActivate: [ParameterGuard],
   *   data: { checkParam: 'userId' }
   * }
   *
   * // URL: /user?userId=456 -> Returns true
   * // URL: /user -> Returns false
   * ```
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this._guard(route);
  }

  /**
   * Determines if a child route can be activated based on the presence of required query parameters.
   *
   * This method is called by the Angular router before activating a child route. It performs the same
   * validation as `canActivate` but for child routes in the route hierarchy.
   *
   * @param childRoute - The activated child route snapshot containing route data and query parameters
   * @param state - The router state snapshot (not used in this guard)
   * @returns `true` if the child route can be activated, `false` if access should be denied, or a `UrlTree` for redirection
   *
   * @example
   * ```typescript
   * // Parent route with child route:
   * {
   *   path: 'parent',
   *   canActivateChild: [ParameterGuard],
   *   children: [
   *     {
   *       path: 'child',
   *       component: ChildComponent,
   *       data: { checkParam: 'token' }
   *     }
   *   ]
   * }
   *
   * // URL: /parent/child?token=abc123 -> Returns true
   * // URL: /parent/child -> Returns false
   * ```
   */
  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this._guard(childRoute);
  }

  /**
   * Internal guard logic that validates query parameter presence.
   *
   * This private method contains the core validation logic:
   * - If no `checkParam` is specified in route data, access is allowed
   * - If `checkParam` is specified but query parameters are missing, access is denied
   * - If the required query parameter exists, access is allowed
   *
   * @param route - The route snapshot to validate
   * @returns `true` if validation passes, `false` otherwise
   *
   * @example
   * ```typescript
   * // Route data: { checkParam: 'code' }
   * // Query params: { code: 'ABC123' } -> Returns true
   * // Query params: {} -> Returns false
   * // Route data: {} -> Returns true (no check required)
   * ```
   */
  _guard(route: ActivatedRouteSnapshot) {
    if (!route.data.checkParam) return true;
    if (!route.queryParams) return false;
    if (route.queryParams[route.data.checkParam]) return true;
    return false;
  }
}
